// Small presentational primitives used across the console. No logic here — just
// tokenised, accessible building blocks (buttons, chips, fields, alerts).

import type { ButtonHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react"
import { CrossIcon } from "./icons.js"

type ButtonVariant = "primary" | "default" | "danger" | "ghost"

export function Button({
  variant = "default",
  small = false,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; small?: boolean }) {
  const classes = ["btn", `btn--${variant}`, small ? "btn--sm" : "", className ?? ""]
    .filter(Boolean)
    .join(" ")
  return <button type="button" className={classes} {...rest} />
}

/**
 * A square, icon-only button.
 *
 * `label` is REQUIRED and is applied as BOTH `aria-label` (so assistive tech
 * announces the action the glyph stands for) and `title` (so a sighted user gets
 * the same word as a hover tooltip). Icons alone are ambiguous; this pairs every
 * glyph with the French action text it replaces.
 */
export function IconButton({
  label,
  variant = "ghost",
  className,
  children,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label" | "title"> & {
  label: string
  variant?: ButtonVariant
}) {
  const classes = ["btn", `btn--${variant}`, "btn--icon", className ?? ""].filter(Boolean).join(" ")
  return (
    <button type="button" className={classes} aria-label={label} title={label} {...rest}>
      {children}
    </button>
  )
}

export function Chip({
  children,
  mono = false,
  system = false,
  title
}: { children: ReactNode; mono?: boolean; system?: boolean; title?: string }) {
  const classes = ["chip", mono ? "chip--mono" : "", system ? "chip--system" : ""]
    .filter(Boolean)
    .join(" ")
  return (
    <span className={classes} title={title}>
      {children}
    </span>
  )
}

/**
 * A chip carrying its own remove affordance — used for assigned roles, so the
 * thing being removed and the control that removes it read as one object.
 *
 * `removeLabel` must name the specific item ("Retirer le rôle Manager"), not
 * just the action: a list of chips yields many remove buttons, and an accessible
 * name of plain "Retirer" would make them indistinguishable to a screen reader.
 */
export function RemovableChip({
  children,
  removeLabel,
  onRemove,
  disabled = false,
  system = false,
  title
}: {
  children: ReactNode
  removeLabel: string
  onRemove: () => void
  disabled?: boolean
  system?: boolean
  title?: string
}) {
  const classes = ["chip", "chip--removable", system ? "chip--system" : ""]
    .filter(Boolean)
    .join(" ")
  return (
    <span className={classes} title={title}>
      {children}
      <button
        type="button"
        className="chip__remove"
        aria-label={removeLabel}
        title={removeLabel}
        disabled={disabled}
        onClick={onRemove}
      >
        <CrossIcon />
      </button>
    </span>
  )
}

export function ToggleChip({
  children,
  selected,
  onToggle,
  title
}: { children: ReactNode; selected: boolean; onToggle: () => void; title?: string }) {
  const classes = ["chip", "chip--mono", "chip--selectable", selected ? "chip--selected" : ""]
    .filter(Boolean)
    .join(" ")
  return (
    <button
      type="button"
      className={classes}
      onClick={onToggle}
      title={title}
      aria-pressed={selected}
    >
      {children}
    </button>
  )
}

export function Card({ children }: { children: ReactNode }) {
  return (
    <div className="card">
      <div className="card__body">{children}</div>
    </div>
  )
}

export function Alert({
  children,
  variant = "info"
}: { children: ReactNode; variant?: "info" | "error" | "warning" }) {
  const cls = variant === "info" ? "alert" : `alert alert--${variant}`
  return (
    <div className={cls} role={variant === "error" ? "alert" : undefined}>
      {children}
    </div>
  )
}

export function Field({
  label,
  htmlFor,
  error,
  children
}: { label: string; htmlFor?: string; error?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? <span className="field__error">{error}</span> : null}
    </div>
  )
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={["field__select", className ?? ""].filter(Boolean).join(" ")} {...rest}>
      {children}
    </select>
  )
}

export function Spinner({ label }: { label?: string }) {
  return <span className="muted">{label ?? "Chargement…"}</span>
}
